import React, { useState, Fragment, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition, Popover } from '@headlessui/react';
import {
  BellIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  TruckIcon,
  Squares2X2Icon,
  ClipboardDocumentCheckIcon,
  PlusCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { motion } from 'framer-motion';
import { formatDateTime } from '../../utils/timezone';

/**
 * Navigation is driven by permissions, not by role names. Add a role on
 * the Roles page, tick page.auctions, and it appears here -- no code change.
 *
 * The previous version had `hidden md:flex` on the links with no mobile
 * fallback, so on a phone the entire nav was invisible. That's the single
 * biggest reason the site was unusable on mobile.
 */
const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: HomeIcon, public: true },
  { to: '/auctions', label: 'Auctions', icon: TruckIcon, requires: 'page.auctions' },
  { to: '/dashboard', label: 'Dashboard', icon: Squares2X2Icon, requires: 'page.dashboard' },
  { to: '/payments', label: 'Payments', icon: CreditCardIcon, requires: 'page.payments' },
  { to: '/approvals', label: 'Approvals', icon: ClipboardDocumentCheckIcon, requires: 'page.approvals' },
  { to: '/auctions/create', label: 'New Auction', icon: PlusCircleIcon, requires: 'page.create_auction' },
  // Shown for ANY admin-dashboard tab permission, not page.admin alone --
  // otherwise a role granted just report.view has no way to navigate there.
  {
    to: '/admin',
    label: 'Admin',
    icon: Cog6ToothIcon,
    requiresAny: [
      'page.admin', 'user.view', 'payment.view_all', 'payment.verify',
      'payment.refund', 'auction.approve', 'announcement.manage',
      'report.view', 'audit.view', 'page.roles',
    ],
  },
];

const Navbar = () => {
  const { user, isAuthenticated, can, canAny, logout, roleName } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the drawer on navigation, otherwise it stays open over the new page.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Don't let the page scroll behind an open drawer.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markAsRead(notification.id);

    if (notification.data?.auction_id) {
      navigate(`/auctions/${notification.data.auction_id}`);
    } else if (notification.data?.transaction_id) {
      navigate('/payments');
    }
  };

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.public) return true;
    if (!isAuthenticated) return false;
    if (item.requiresAny) return canAny(item.requiresAny);
    return can(item.requires);
  });

  const isCurrent = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const notificationIcon = (type) =>
    ({
      outbid: '🔔',
      bid_received: '💰',
      auction_won: '🏆',
      payment_received: '💳',
      refund_processed: '↩️',
      auction_ended: '⏰',
      auction_upcoming: '📅',
    }[type] || '📌');

  return (
    <nav className="bg-[#084f3c] shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo — the old h-[150px] overflowed a 64px bar and pushed
              everything off-screen on phones. */}
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img
              src="/logo.png"
              alt="Salvage Portal"
              className="h-9 w-9 sm:h-11 sm:w-11 object-contain flex-shrink-0"
            />
            <span className="font-bold text-base sm:text-xl text-white truncate">
              <span className="hidden xs:inline sm:inline">Salvages </span>Management
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-6">
            {visibleItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`text-sm transition ${
                  isCurrent(item.to)
                    ? 'text-white font-semibold border-b-2 border-white pb-1'
                    : 'text-white/85 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-3">
            {isAuthenticated ? (
              <>
                {/* Notifications */}
                <Popover className="relative">
                  <Popover.Button className="relative p-2 rounded-full hover:bg-[#0f5a38] focus:outline-none focus:ring-2 focus:ring-white/40">
                    <BellIcon className="h-6 w-6 text-white" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 translate-x-1 -translate-y-1 bg-red-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Popover.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    {/* Full width minus gutters on phones so it can't
                        overflow the viewport. */}
                    <Popover.Panel className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 sm:w-80 mt-2 bg-white rounded-lg shadow-xl overflow-hidden z-50">
                      <div className="p-4 border-b flex justify-between items-center">
                        <h3 className="font-semibold">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-sm text-[#084f3c] hover:underline"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>

                      <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-6 text-center text-gray-500">
                            No notifications
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <motion.div
                              key={notification.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className={`p-4 border-b active:bg-gray-100 hover:bg-gray-50 cursor-pointer ${
                                !notification.is_read ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="h-8 w-8 rounded-full bg-[#084f3c]/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm">
                                    {notificationIcon(notification.type)}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {notification.title}
                                  </p>
                                  <p className="text-sm text-gray-500 line-clamp-2">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {formatDateTime(notification.created_at, 'short')}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          ))
                        )}
                      </div>

                      <div className="p-3 bg-gray-50 border-t">
                        <Link
                          to="/notifications"
                          className="block text-center text-sm text-[#084f3c] hover:underline font-medium"
                        >
                          View All Notifications →
                        </Link>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </Popover>

                {/* User menu — desktop only; on mobile it lives in the drawer */}
                <Menu as="div" className="relative hidden sm:block">
                  <Menu.Button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[#0f5a38] focus:outline-none focus:ring-2 focus:ring-white/40">
                    <div className="h-8 w-8 rounded-full bg-white text-[#084f3c] flex items-center justify-center font-bold text-sm">
                      {user?.first_name?.[0] || user?.username?.[0] || 'U'}
                    </div>
                    <div className="hidden md:block text-left leading-tight">
                      <div className="text-sm font-medium text-white">
                        {user?.first_name || user?.username}
                      </div>
                      {roleName && roleName !== 'bidder' && (
                        <div className="text-[11px] text-white/70 capitalize">
                          {roleName}
                        </div>
                      )}
                    </div>
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-52 bg-white rounded-md shadow-lg py-1 z-50">
                      <div className="px-4 py-2 border-b">
                        <p className="text-sm font-medium truncate">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>

                      {can('page.dashboard') && (
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/dashboard"
                              className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                            >
                              Dashboard
                            </Link>
                          )}
                        </Menu.Item>
                      )}

                      {can('page.payments') && (
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/payments"
                              className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <CreditCardIcon className="h-4 w-4 mr-2 text-gray-500" />
                              Payments
                            </Link>
                          )}
                        </Menu.Item>
                      )}

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`${active ? 'bg-gray-100' : ''} flex items-center w-full text-left px-4 py-2 text-sm text-red-600`}
                          >
                            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                            Logout
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>

                {/* Hamburger */}
                <button
                  onClick={() => setMobileOpen((open) => !open)}
                  aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileOpen}
                  className="lg:hidden p-2 rounded-lg text-white hover:bg-[#0f5a38] focus:outline-none focus:ring-2 focus:ring-white/40"
                >
                  {mobileOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2 sm:gap-4">
                <Link
                  to="/login"
                  className="text-white hover:text-gray-200 text-sm sm:text-base px-2 py-2"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-white text-[#084f3c] px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-100 text-sm sm:text-base font-medium"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <Transition
        show={mobileOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-2"
      >
        <div className="lg:hidden border-t border-white/15 bg-[#084f3c] max-h-[calc(100vh-3.5rem)] overflow-y-auto">
          <div className="px-3 py-3 space-y-1">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  // min-h-[44px]: Apple's minimum touch target. The old
                  // links were ~20px tall and genuinely hard to hit.
                  className={`flex items-center gap-3 px-3 min-h-[44px] rounded-lg text-white ${
                    isCurrent(item.to) ? 'bg-white/15 font-semibold' : 'hover:bg-white/10'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {item.label}
                </Link>
              );
            })}

            {isAuthenticated && (
              <div className="pt-3 mt-2 border-t border-white/15">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-white/60 truncate">{user?.email}</p>
                  {roleName && (
                    <span className="inline-block mt-1.5 px-2 py-0.5 text-[11px] bg-white/15 text-white rounded-full capitalize">
                      {roleName}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-3 min-h-[44px] rounded-lg text-red-200 hover:bg-white/10"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </Transition>
    </nav>
  );
};

export default Navbar;
